/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { parseDateOnly } from '../utils/et';
import { format } from 'date-fns';
import openFile from '../utils/openFile';

const OUT_OF_SERVICE_REASONS = [
  'Accident damage',
  'Scheduled maintenance',
  'Inspection',
  'Mechanical issue',
  'Cleaning/sanitization',
  'Other',
];

const VEHICLE_DOTS = {
  'MBW0677': '#2C2C2A',
  'LZK3338': '#888780',
  'LWR7731': '#0F6E56',
  'MGJ5613': '#993C1D',
  'MGJ5622': '#444441',
  'BA89464': '#B4B2A9',
  'MPJ4505': '#D3D1C7',
  'NFV6990': '#8E8D85',
};

// Inspection sticker status: expired = red, due within 60 days = amber.
function inspectionDisplay(dateStr) {
  if (!dateStr) return null;
  const exp = new Date(dateStr);
  const label = `Insp ${exp.getUTCMonth() + 1}/${String(exp.getUTCFullYear()).slice(2)}`;
  const daysLeft = (exp - Date.now()) / 86400000;
  if (daysLeft < 0) return { label: label + ' EXPIRED', color: '#A33326', weight: 600 };
  if (daysLeft < 60) return { label: label + ' due soon', color: '#854F0B', weight: 600 };
  return { label, color: '#aaa', weight: 400 };
}

const DEFAULT_DOTS = ['#185FA5', '#1D9E75', '#BA7517', '#993C1D', '#534AB7'];

function Badge({ bg, color, label }) {
  return (
    <span style={{ background: bg, color, fontSize: 10, padding: '2px 7px', borderRadius: 20, fontWeight: 500, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

function AddVehicleModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', licensePlate: '', vehicleType: 'wheelchair_van', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!form.name || !form.licensePlate) { setError('Name and license plate are required'); return; }
    setSaving(true);
    try {
      await api.post('/vehicles', { name: form.name, licensePlate: form.licensePlate, vehicleType: form.vehicleType, notes: form.notes });
      onSaved();
      onClose();
    } catch (err) {
      setError('Could not add vehicle — ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div style={{ background: 'white', borderRadius: 12, padding: '1.5rem', width: '100%', maxWidth: 380, margin: '0 16px' }}>
        <h2 style={{ fontSize: 16, fontWeight: 500, marginBottom: 16 }}>Add vehicle</h2>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Vehicle name</label>
          <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="e.g. White Wheelchair 3" style={{ width: '100%' }} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>License plate</label>
          <input value={form.licensePlate} onChange={e => setForm(f => ({...f, licensePlate: e.target.value}))} placeholder="e.g. ABC1234" style={{ width: '100%' }} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Type</label>
          <select value={form.vehicleType} onChange={e => setForm(f => ({...f, vehicleType: e.target.value}))} style={{ width: '100%' }}>
            <option value="wheelchair_van">Wheelchair van</option>
            <option value="sedan">Sedan</option>
          </select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Notes (optional)</label>
          <input value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="e.g. Toyota Sienna" style={{ width: '100%' }} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ fontSize: 13 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ fontSize: 13, background: '#185FA5', color: 'white', border: '0.5px solid #185FA5', padding: '6px 16px' }}>
            {saving ? 'Saving...' : 'Add vehicle'}
          </button>
        </div>
      </div>
    </div>
  );
}

function VehicleCard({ vehicle, onStatusChange }) {
  const [showEdit, setShowEdit] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [renameForm, setRenameForm] = useState({ name: vehicle.name, licensePlate: vehicle.license_plate, vehicleType: vehicle.vehicle_type });
  const [savingRename, setSavingRename] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [showAddMaint, setShowAddMaint] = useState(false);
  const [maintForm, setMaintForm] = useState({ maintenanceType: '', description: '', shopName: '', cost: '', odometerAtService: '', maintenanceDate: '', receiptFile: null });
  const [savingMaint, setSavingMaint] = useState(false);
  const [showFuel, setShowFuel] = useState(false);
  const [fuelLogs, setFuelLogs] = useState([]);
  const [fuelStats, setFuelStats] = useState(null);
  const [mode, setMode] = useState('out');
  const [reason, setReason] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const isOutOfService = vehicle.status !== 'active';
  const dot = VEHICLE_DOTS[vehicle.license_plate] || DEFAULT_DOTS[0];

  const fetchMaintenance = async () => {
    try {
      const r = await api.get(`/vehicles/${vehicle.id}/maintenance`);
      setMaintenanceRecords(r.data.records || []);
    } catch (e) {}
  };

  const fetchFuel = async () => {
    try {
      const r = await api.get(`/vehicles/${vehicle.id}/fuel-logs`);
      setFuelLogs(r.data.logs || []);
      setFuelStats(r.data.stats || null);
    } catch (e) {}
  };

  const handleAddMaintenance = async () => {
    setSavingMaint(true);
    try {
      // Upload receipt first if provided
      let receiptFileId = null;
      if (maintForm.receiptFile) {
        const formData = new FormData();
        formData.append('file', maintForm.receiptFile);
        formData.append('category', 'maintenance');
        formData.append('docType', 'maintenance_receipt');
        const uploadRes = await api.post('/files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        receiptFileId = uploadRes.data.file.id;
      }
      await api.post(`/vehicles/${vehicle.id}/maintenance`, { ...maintForm, receiptFileId, receiptFile: undefined });
      setShowAddMaint(false);
      setMaintForm({ maintenanceType: '', description: '', shopName: '', cost: '', odometerAtService: '', maintenanceDate: '', receiptFile: null });
      fetchMaintenance();
    } catch (e) { alert('Failed to add'); }
    finally { setSavingMaint(false); }
  };

  const handleSave = async (newStatus) => {
    setSaving(true);
    try {
      let noteText = notes;
      if (!noteText && reason) {
        noteText = reason;
        if (mode === 'scheduled' && startDate) {
          noteText += ` — scheduled ${startDate}`;
          if (startTime) noteText += ` from ${startTime}`;
          if (endTime) noteText += ` to ${endTime}`;
          if (endDate && endDate !== startDate) noteText += ` (returns ${endDate})`;
        } else if (endDate) {
          noteText += ` — est. return ${endDate}`;
        }
      }
      await api.put(`/vehicles/${vehicle.id}`, { status: newStatus, notes: noteText || null });
      onStatusChange();
      setShowEdit(false);
      setReason(''); setStartDate(''); setEndDate('');
      setStartTime(''); setEndTime(''); setNotes('');
    } catch (err) {
      alert('Could not update vehicle — ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: 'white', border: `0.5px solid ${isOutOfService ? '#FAC775' : '#e0e0db'}`, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: isOutOfService ? '#FAEEDA22' : '#f9f9f8', borderBottom: '0.5px solid #f0f0f0' }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: dot, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>{vehicle.name}</div>
            <button onClick={() => setShowRename(!showRename)} style={{ fontSize: 10, padding: '1px 6px', color: '#999', background: 'none', border: '0.5px solid #e0e0db', borderRadius: 4, cursor: 'pointer' }}>Edit</button>
          </div>
          <div style={{ fontSize: 11, color: '#aaa', marginTop: 1 }}>
            {vehicle.license_plate}
            {(() => {
              const insp = inspectionDisplay(vehicle.inspection_expires);
              return insp ? <span style={{ marginLeft: 8, color: insp.color, fontWeight: insp.weight }}>{insp.label}</span> : null;
            })()}
            {vehicle.has_ezpass != null && (
              <span style={{ marginLeft: 8, color: vehicle.has_ezpass ? '#0F6E56' : '#854F0B' }}>
                {vehicle.has_ezpass ? 'EZPass' : 'no EZPass'}
              </span>
            )}
          </div>
        </div>
        <Badge
          bg={vehicle.vehicle_type === 'sedan' ? '#E6F1FB' : '#EEEDFE'}
          color={vehicle.vehicle_type === 'sedan' ? '#0C447C' : '#3C3489'}
          label={vehicle.vehicle_type === 'sedan' ? 'Sedan' : 'Wheelchair'}
        />
        {isOutOfService
          ? <Badge bg="#FAEEDA" color="#854F0B" label="Out of service" />
          : <Badge bg="#E1F5EE" color="#0F6E56" label="Active" />
        }
      </div>

      <div style={{ padding: '10px 16px' }}>
        {showRename && (
          <div style={{ background: '#f9f9f8', padding: '10px', borderRadius: 8, marginBottom: 10 }}>
            <div style={{ marginBottom: 6 }}>
              <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 2 }}>Name</label>
              <input value={renameForm.name} onChange={e => setRenameForm(f => ({...f, name: e.target.value}))} style={{ width: '100%', fontSize: 12 }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
              <div>
                <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 2 }}>Plate</label>
                <input value={renameForm.licensePlate} onChange={e => setRenameForm(f => ({...f, licensePlate: e.target.value}))} style={{ width: '100%', fontSize: 12 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 2 }}>Type</label>
                <select value={renameForm.vehicleType} onChange={e => setRenameForm(f => ({...f, vehicleType: e.target.value}))} style={{ width: '100%', fontSize: 12 }}>
                  <option value="wheelchair_van">Wheelchair van</option>
                  <option value="sedan">Sedan</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setShowRename(false)} style={{ fontSize: 11 }}>Cancel</button>
              <button disabled={savingRename} onClick={async () => {
                setSavingRename(true);
                try {
                  await api.put(`/vehicles/${vehicle.id}`, { name: renameForm.name, licensePlate: renameForm.licensePlate, vehicleType: renameForm.vehicleType });
                  onStatusChange();
                  setShowRename(false);
                } catch (e) { alert('Could not update: ' + (e.response?.data?.error || e.message)); }
                finally { setSavingRename(false); }
              }} style={{ fontSize: 11, background: '#185FA5', color: 'white', border: 'none', padding: '3px 10px' }}>
                {savingRename ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
        {vehicle.notes && (
          <div style={{ fontSize: 12, color: isOutOfService ? '#854F0B' : '#666', marginBottom: 8 }}>
            {vehicle.notes}
          </div>
        )}

        {!showEdit ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {isOutOfService ? (
              <button onClick={() => handleSave('active')} style={{ fontSize: 12, padding: '4px 12px', background: '#E1F5EE', color: '#0F6E56', border: '0.5px solid #5DCAA5' }}>
                Mark active
              </button>
            ) : (
              <>
                <button onClick={() => { setMode('out'); setShowEdit(true); }} style={{ fontSize: 12, padding: '4px 12px' }}>
                  Take out of service
                </button>
                <button onClick={() => { setMode('scheduled'); setShowEdit(true); }} style={{ fontSize: 12, padding: '4px 12px', background: '#E6F1FB', color: '#185FA5', border: '0.5px solid #B5D4F4' }}>
                  Schedule maintenance
                </button>
              </>
            )}
          </div>
        ) : (
          <div>
            {mode === 'scheduled' && (
              <div style={{ background: '#E6F1FB', padding: '8px 10px', borderRadius: 8, fontSize: 12, color: '#185FA5', marginBottom: 10 }}>
                This will note the scheduled date. Mark it out of service when it actually goes in.
              </div>
            )}
            <div style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Reason</label>
              <select value={reason} onChange={e => setReason(e.target.value)} style={{ width: '100%' }}>
                <option value="">Select reason...</option>
                {OUT_OF_SERVICE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            {mode === 'scheduled' && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Date</label>
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ width: '100%' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Return date (if multi-day)</label>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ width: '100%' }} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Drop off time</label>
                    <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={{ width: '100%' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Pick up time</label>
                    <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} style={{ width: '100%' }} />
                  </div>
                </div>
              </div>
            )}
            {mode === 'out' && (
              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Est. return date (optional)</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ width: '100%' }} />
              </div>
            )}
            <div style={{ marginBottom: 10 }}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Additional notes (optional)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={{ width: '100%', resize: 'none' }} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowEdit(false)} style={{ fontSize: 12 }}>Cancel</button>
              {mode === 'scheduled' ? (
                <button onClick={() => handleSave('active')} disabled={saving} style={{ fontSize: 12, background: '#E6F1FB', color: '#185FA5', border: '0.5px solid #B5D4F4' }}>
                  {saving ? 'Saving...' : 'Save schedule'}
                </button>
              ) : (
                <button onClick={() => handleSave('maintenance')} disabled={saving} style={{ fontSize: 12, background: '#FAEEDA', color: '#854F0B', border: '0.5px solid #FAC775' }}>
                  {saving ? 'Saving...' : 'Mark out of service'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Fuel History */}
        <div style={{ borderTop: '0.5px solid #f0f0f0', padding: '8px 0 0', marginBottom: 8 }}>
          <button onClick={() => { setShowFuel(!showFuel); if (!showFuel) fetchFuel(); }}
            style={{ fontSize: 11, color: '#0F6E56', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            {showFuel ? '▲ Hide fuel history' : '⛽ Fuel history'}
          </button>

          {showFuel && (
            <div style={{ marginTop: 8 }}>
              {fuelStats && (fuelStats.fillCount > 0) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 8 }}>
                  <div style={{ background: '#E1F5EE', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                    <div style={{ fontSize: 9, color: '#0F6E56' }}>Total spent</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0F6E56' }}>${fuelStats.totalCost.toFixed(0)}</div>
                  </div>
                  <div style={{ background: '#E6F1FB', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                    <div style={{ fontSize: 9, color: '#0C447C' }}>Gallons</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0C447C' }}>{fuelStats.totalGallons}</div>
                  </div>
                  <div style={{ background: '#EEEDFE', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                    <div style={{ fontSize: 9, color: '#3C3489' }}>Avg MPG</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#3C3489' }}>{fuelStats.avgMpg || '—'}</div>
                  </div>
                </div>
              )}
              {fuelLogs.length === 0 ? (
                <div style={{ fontSize: 11, color: '#999', fontStyle: 'italic' }}>No fuel records yet. Drivers submit gas receipts from their trip expenses.</div>
              ) : (
                fuelLogs.map(l => (
                  <div key={l.id} style={{ borderBottom: '0.5px solid #f0f0f0', padding: '5px 0', fontSize: 11 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 500 }}>
                        {l.cost_total ? `$${Number(l.cost_total).toFixed(2)}` : '—'}
                        {l.gallons ? ` · ${Number(l.gallons).toFixed(1)} gal` : ''}
                      </span>
                      <span style={{ color: '#999' }}>{l.fill_date ? format(parseDateOnly(l.fill_date), 'MMM d') : '--'}</span>
                    </div>
                    <div style={{ color: '#888', marginTop: 1 }}>
                      {l.driver_name || ''}
                      {l.cost_per_gallon ? ` · $${Number(l.cost_per_gallon).toFixed(3)}/gal` : ''}
                      {l.odometer_at_fill ? ` · ${Number(l.odometer_at_fill).toLocaleString()} mi` : ''}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Service History */}
        <div style={{ borderTop: '0.5px solid #f0f0f0', padding: '8px 0 0' }}>
          <button onClick={() => { setShowMaintenance(!showMaintenance); if (!showMaintenance) fetchMaintenance(); }}
            style={{ fontSize: 11, color: '#185FA5', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            {showMaintenance ? '▲ Hide service history' : '▼ Service history'}
          </button>

          {showMaintenance && (
            <div style={{ marginTop: 8 }}>
              <button onClick={() => setShowAddMaint(!showAddMaint)}
                style={{ fontSize: 11, padding: '3px 10px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 4, marginBottom: 8 }}>
                + Add record
              </button>

              {showAddMaint && (
                <div style={{ background: '#f9f9f8', padding: '10px', borderRadius: 8, marginBottom: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
                    <select value={maintForm.maintenanceType} onChange={e => setMaintForm(f => ({...f, maintenanceType: e.target.value}))} style={{ fontSize: 11 }}>
                      <option value="">Type...</option>
                      {['Oil change','Tire rotation','Tire replacement','Brake service','Battery replacement',
                        'Transmission service','Inspection','Wheelchair lift service','Body repair','AC/heating',
                        'Electrical','Suspension','Windshield/glass','General service','Other'].map(t =>
                        <option key={t} value={t}>{t}</option>
                      )}
                    </select>
                    <input type="date" value={maintForm.maintenanceDate} onChange={e => setMaintForm(f => ({...f, maintenanceDate: e.target.value}))} style={{ fontSize: 11 }} />
                  </div>
                  <input value={maintForm.shopName} onChange={e => setMaintForm(f => ({...f, shopName: e.target.value}))} placeholder="Shop name" style={{ width: '100%', fontSize: 11, marginBottom: 6 }} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
                    <input type="number" step="0.01" value={maintForm.cost} onChange={e => setMaintForm(f => ({...f, cost: e.target.value}))} placeholder="Cost $" style={{ fontSize: 11 }} />
                    <input type="number" step="0.1" value={maintForm.odometerAtService} onChange={e => setMaintForm(f => ({...f, odometerAtService: e.target.value}))} placeholder="Odometer" style={{ fontSize: 11 }} />
                  </div>
                  <textarea value={maintForm.description} onChange={e => setMaintForm(f => ({...f, description: e.target.value}))} placeholder="Description of work..." rows={2} style={{ width: '100%', fontSize: 11, resize: 'none', marginBottom: 6 }} />
                  <div style={{ marginBottom: 6 }}>
                    <label style={{
                      fontSize: 11, padding: '4px 10px', background: '#f0f0f0',
                      border: '0.5px solid #e0e0db', borderRadius: 4, cursor: 'pointer',
                    }}>
                      {maintForm.receiptFile ? `📎 ${maintForm.receiptFile.name}` : '📎 Attach receipt'}
                      <input type="file" accept="image/*,.pdf" onChange={e => setMaintForm(f => ({...f, receiptFile: e.target.files?.[0] || null}))} style={{ display: 'none' }} />
                    </label>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => setShowAddMaint(false)} style={{ fontSize: 11 }}>Cancel</button>
                    <button onClick={handleAddMaintenance} disabled={savingMaint || !maintForm.maintenanceType}
                      style={{ fontSize: 11, background: '#1D9E75', color: 'white', border: 'none', padding: '3px 10px' }}>
                      {savingMaint ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              )}

              {maintenanceRecords.length === 0 ? (
                <div style={{ fontSize: 11, color: '#999', fontStyle: 'italic' }}>No service records yet.</div>
              ) : (
                maintenanceRecords.map(r => (
                  <div key={r.id} style={{ borderBottom: '0.5px solid #f0f0f0', padding: '6px 0', fontSize: 11 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 500 }}>{r.maintenance_type}</span>
                      <span style={{ color: '#999' }}>{r.maintenance_date ? format(parseDateOnly(r.maintenance_date), 'MMM d, yyyy') : '--'}</span>
                    </div>
                    {r.shop_name && <div style={{ color: '#666' }}>{r.shop_name}</div>}
                    {r.description && <div style={{ color: '#666' }}>{r.description}</div>}
                    <div style={{ color: '#999', marginTop: 2 }}>
                      {r.cost ? `$${Number(r.cost).toFixed(2)}` : ''}
                      {r.odometer_at_service ? ` · ${r.odometer_at_service} mi` : ''}
                      {r.receipt_file_id && (
                        <a href="#" onClick={(e) => openFile(r.receipt_file_id, e)}
                          style={{ marginLeft: 6, fontSize: 10, color: '#185FA5' }}>📎 Receipt</a>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Daily walk-arounds (8/10): the videos Chance used to collect in the group
// text now live on the day's vehicle check-in. This panel is his review spot.
function WalkaroundPanel() {
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/drivers/walkarounds/list?date=${date}`)
      .then(r => setRows(r.data.walkarounds || []))
      .catch(() => setRows([]));
  }, [date]);
  const withVideo = rows.filter(r => r.video_file_id).length;
  return (
    <div style={{ background: 'white', border: '0.5px solid #e0e0db', borderRadius: 12, marginBottom: 16 }}>
      <div style={{ padding: '12px 16px', borderBottom: '0.5px solid #e0e0db', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>
          Daily walk-arounds <span style={{ color: '#999', fontWeight: 400 }}>({withVideo}/{rows.length} with video)</span>
        </div>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ fontSize: 12, padding: '4px 8px' }} />
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: '16px', fontSize: 13, color: '#999', textAlign: 'center' }}>No check-ins for this date.</div>
      ) : rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: i < rows.length - 1 ? '0.5px solid #f0f0f0' : 'none', fontSize: 13 }}>
          <div style={{ flex: 1 }}>
            <strong>{r.vehicle_name}</strong>
            <span style={{ color: '#666' }}> — {r.driver_name}, {r.at_et}</span>
            {r.issues_noted && <div style={{ fontSize: 12, color: '#993C1D' }}>Issues: {r.issues_noted}</div>}
          </div>
          {r.video_file_id ? (
            <a href="#video" onClick={(e) => openFile(r.video_file_id, e)}
              style={{ fontSize: 12, fontWeight: 600, color: 'white', background: '#185FA5', borderRadius: 6, padding: '6px 12px', textDecoration: 'none' }}>
              {'▶'} Video
            </a>
          ) : (
            <span style={{ fontSize: 11, color: '#BA7517', background: '#FAEEDA', borderRadius: 10, padding: '3px 10px' }}>no video</span>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchVehicles = async () => {
    try {
      const res = await api.get('/vehicles');
      setVehicles(res.data.vehicles || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVehicles(); }, []);

  const activeCount = vehicles.filter(v => v.status === 'active').length;
  const outCount = vehicles.filter(v => v.status !== 'active').length;

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 500, margin: 0 }}>Vehicles</h1>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{activeCount} active · {outCount} out of service</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={fetchVehicles} style={{ fontSize: 12 }}>Refresh</button>
          <button onClick={() => setShowAddModal(true)} style={{ fontSize: 12, background: '#185FA5', color: 'white', border: '0.5px solid #185FA5', padding: '6px 14px' }}>
            + Add vehicle
          </button>
        </div>
      </div>

      <WalkaroundPanel />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10, marginBottom: 16 }}>
        {[
          { label: 'Active vehicles', value: activeCount, color: '#1D9E75' },
          { label: 'Out of service', value: outCount, color: outCount > 0 ? '#BA7517' : '#666' },
          { label: 'Total fleet', value: vehicles.length, color: '#185FA5' },
        ].map(s => (
          <div key={s.label} style={{ background: '#f5f5f3', borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 500, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div style={{ color: '#999', fontSize: 13 }}>Loading...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
          {vehicles.map(vehicle => (
            <VehicleCard key={vehicle.id} vehicle={vehicle} onStatusChange={fetchVehicles} />
          ))}
        </div>
      )}

      {showAddModal && <AddVehicleModal onClose={() => setShowAddModal(false)} onSaved={fetchVehicles} />}
    </div>
  );
}